import moment from 'moment'
import type { CategoryResolver } from 'lib/cdtCodeDefaults'
import { isTreatmentCode } from 'lib/cdtCodeDefaults'
import type { BookedGroup, BookedGroupDef, VisitKind } from 'app/types/treatment'
import { isBroken, isDeleted } from 'lib/treatmentCards'

/** The four groups partition the band; hiding all of them empties it. */
export const BOOKED_GROUPS: BookedGroupDef[] = [
  {
    id: `treatment`,
    label: `Booked for treatment`,
    blurb: `Their next visit carries something beyond a cleaning. The plan is already moving.`,
  },
  {
    id: `hygiene`,
    label: `Booked for hygiene only`,
    blurb: `Their next visit is a cleaning or exam with none of the planned work on it.`,
  },
  {
    id: `broken`,
    label: `Broken appointment`,
    blurb: `Their last appointment broke or was cancelled and nothing has been rebooked.`,
  },
  {
    id: `none`,
    label: `Nothing booked`,
    blurb: `No upcoming appointment of any kind.`,
  },
]

/**
 * Not one of the partition: a new patient can sit in any booked group. Their
 * plan was made when the first visit was booked, so chasing it as unaccepted
 * treatment is a call about a conversation that has not happened yet.
 */
export const UNSEEN_GROUP: BookedGroupDef = {
  id: `unseen`,
  label: `Never seen`,
  blurb: `No completed visit on record. Usually a new patient whose plan was made before their first appointment.`,
}

const HIDEABLE: BookedGroup[] = [...BOOKED_GROUPS.map((group) => group.id), UNSEEN_GROUP.id]

/** Unknown words are dropped rather than erroring: the param is typed and shared. */
export function hiddenFromParam(value: string | null): BookedGroup[] {
  const named = new Set((value ?? ``).split(`,`))
  return HIDEABLE.filter((id) => named.has(id))
}

/**
 * The groups a board leaves out, read from its Filters rules. `visit` names next-visit groups,
 * kept under `in` (no `visitOp`) and left out under `not_in`; `in` with none named narrows
 * nothing. `seen=yes` keeps only patients with a completed visit, `seen=no` only those without.
 * `hide` is the older param those two replaced, still read so a saved link opens the same board.
 */
export function hiddenFromRules(params: URLSearchParams): BookedGroup[] {
  const named = new Set((params.get(`visit`) ?? ``).split(`,`))
  const booked = BOOKED_GROUPS.map((group) => group.id)
  const picked = booked.filter((id) => named.has(id))
  const leftOut =
    params.get(`visitOp`) === `not_in` ? picked : picked.length ? booked.filter((id) => !named.has(id)) : []
  const seen = params.get(`seen`)
  const history: BookedGroup[] = seen === `yes` ? [`unseen`] : seen === `no` ? [`seen`] : []
  return Array.from(new Set([...hiddenFromParam(params.get(`hide`)), ...leftOut, ...history]))
}

type FutureVisit = {
  patientId?: string | null
  procedureCodes?: string[] | null
  status?: string | null
  pmsStatus?: string | null
}

/**
 * Broken and deleted visits are skipped: they say nothing about what somebody
 * is booked for. A patient reads as treatment when any code passes
 * `isTreatmentCode`, the same test the board uses, so the workspace mapping decides:
 * Uncategorized-only and Adjunctive-only visits are hygiene. A visit without
 * codes is not evidence of a cleaning, so one coded hygiene visit beside an
 * uncoded one still reads as hygiene, and only uncoded visits read as `unknown`.
 */
export function futureVisitKinds(visits: FutureVisit[], categoryOf: CategoryResolver): Map<string, VisitKind> {
  const codesByPatient = new Map<string, string[]>()
  for (const visit of visits) {
    if (!visit.patientId || isBroken(visit) || isDeleted(visit)) continue
    const codes = codesByPatient.get(visit.patientId) ?? []
    codes.push(...(visit.procedureCodes ?? []))
    codesByPatient.set(visit.patientId, codes)
  }
  const kinds = new Map<string, VisitKind>()
  for (const [patientId, codes] of codesByPatient) {
    if (codes.length === 0) kinds.set(patientId, `unknown`)
    else kinds.set(patientId, codes.some((code) => isTreatmentCode(code, categoryOf)) ? `treatment` : `hygiene`)
  }
  return kinds
}

/**
 * A contact can carry several patient records; a treatment visit on any of
 * them wins. `unknown` (no codes on the visit) counts as treatment.
 */
export function bookedGroupOf(
  contact: { patientIds?: string[] | null },
  kinds: Map<string, VisitKind>,
): `treatment` | `hygiene` | undefined {
  const mine = (contact.patientIds ?? []).map((patientId) => kinds.get(patientId))
  if (mine.includes(`treatment`)) return `treatment`
  if (mine.includes(`hygiene`)) return `hygiene`
  if (mine.includes(`unknown`)) return `treatment`
  return undefined
}

/**
 * Reads the contact rollup: `nearestAppointmentDate` is the nearest live
 * future visit, else the most recent past one, and `appointmentStatus` is
 * that visit's status (the API's `findNearestAppointment`). Verified against
 * the appointment records at Hamblin, 2026-09-03.
 *
 * The index cannot tell a cleaning from a crown, so the two booked groups
 * are one group to it: with only one of them hidden it leaves everyone
 * booked in and `useScheduleSplit` finishes the job. Passing the qualifying
 * patients as a `patientIds` term was tried and fails past 1,024 clauses
 * (Hamblin has 2,080).
 *
 * Hidden groups are subtracted from `*:*`: a group led by a negation
 * collapses to nothing rather than erroring. "Nothing booked" is itself a
 * negation, so hiding it is written as the union of what stays.
 *
 * `lastAppointmentDate` is set only by a completed visit (a patient with only
 * a broken one carries none), so hiding the never-seen is one positive term,
 * and hiding the seen is that term subtracted from `*:*` like the groups above.
 */
export function hiddenSearchTerms(hidden: BookedGroup[], now: moment.Moment = moment.utc()): string[] {
  const today = now.clone().format(`YYYY-MM-DD`)
  const booked = `(nearestAppointmentDate:[${today} TO *] AND appointmentStatus.keyword:"Scheduled")`
  const broken = `appointmentStatus.keyword:("Broken" OR "Cancelled")`
  const hideBooked = hidden.includes(`treatment`) && hidden.includes(`hygiene`)
  const hideBroken = hidden.includes(`broken`)
  const seen = [
    ...(hidden.includes(`unseen`) ? [`lastAppointmentDate:*`] : []),
    ...(hidden.includes(`seen`) ? [`(*:* !lastAppointmentDate:*)`] : []),
  ]

  if (hidden.includes(`none`)) {
    const showing = [...(hideBooked ? [] : [booked]), ...(hideBroken ? [] : [broken])]
    if (showing.length === 0) throw new Error(`Every booked group is hidden; nothing to search for`)
    return [`(${showing.join(` OR `)})`, ...seen]
  }
  const taken = [...(hideBooked ? [booked] : []), ...(hideBroken ? [broken] : [])]
  return [...(taken.length ? [`(*:* ${taken.map((clause) => `!${clause}`).join(` `)})`] : []), ...seen]
}
